import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import ExtranetService, { ListExtranetPendingChangesParams } from '~/services/ExtranetService';

export const extranetPendingChangesQueryKeys = {
  all: ['extranet', 'pendingChanges'] as const,
  lists: () => [...extranetPendingChangesQueryKeys.all, 'list'] as const,
  list: (filters?: ListExtranetPendingChangesParams) =>
    [...extranetPendingChangesQueryKeys.lists(), filters ?? {}] as const,
  detail: (id: string) => [...extranetPendingChangesQueryKeys.all, 'detail', id] as const,
};

export function useGetExtranetPendingChanges(filters?: ListExtranetPendingChangesParams) {
  return useQuery({
    queryKey: extranetPendingChangesQueryKeys.list(filters),
    queryFn: ({ signal }) => ExtranetService.listPendingChanges(filters, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function useGetExtranetPendingChange(id: string) {
  return useQuery({
    queryKey: extranetPendingChangesQueryKeys.detail(id),
    queryFn: ({ signal }) => ExtranetService.getPendingChangeById(id, { signal }),
    enabled: !!id,
  });
}

export function useApproveExtranetChange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ExtranetService.approveChange(id),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: extranetPendingChangesQueryKeys.all });
    },
  });
}

export function useRejectExtranetChange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ExtranetService.rejectChange(id),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: extranetPendingChangesQueryKeys.all });
    },
  });
}

export function useBulkApproveExtranetChanges() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => ExtranetService.bulkApprove(ids),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: extranetPendingChangesQueryKeys.all });
    },
  });
}

export function useBulkRejectExtranetChanges() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => ExtranetService.bulkReject(ids),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: extranetPendingChangesQueryKeys.all });
    },
  });
}
